import { faEye } from '@fortawesome/free-regular-svg-icons'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Switch, useRouteMatch } from 'react-router-dom'
import AppContext from '../../Authentication/AppContext'
import Authorization from '../../Authentication/Authorization'
import AuthService from '../../Authentication/AuthService'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import ProtectedButton from '../../components/ProtectedButton'
import ProtectedRoute from '../../components/ProtectedRoute'
import Loading from '../../layout/Loading'
import AppService from '../../service/AppService'
import { getDataField } from '../../utils/DialogUtil'
import GatewayType from '../GatewayType'

const Apps: GatewayType = () => {
  const match = useRouteMatch()
  const [apps, setApps] = useState([] as any[])
  const [appDefinitions, setAppDefinitions] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)
  const { t } = useTranslation()

  useEffect(() => {
    async function loadData() {
      const apps = await AppService.getApps()
      const definitions = await AppService.getDefinitions()
      setApps(apps)
      setAppDefinitions(definitions)
    }
    loadData().finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: 'Name',
        dataField: 'name',
        classes: 'position-relative',
        sort: true,
        formatter: (cellData: any, row: any) => (
          <ProtectedButton variant='link' className='text-decoration-none pl-0 stretched-link text-left' tooltip={t('view')} href={`/apps/${row.id}`}>
            <FontAwesomeIcon icon={faEye} /> {cellData}
          </ProtectedButton>
        ),
      },
      {
        text: 'Type',
        dataField: 'type',
        sort: true,
      },
      {
        text: 'Authentication Method',
        dataField: 'authmethod',
        sort: true,
      },
      {
        hidden: !AuthService.hasPermission(userPermissions, Authorization.apps.edit),
        dataField: 'df1',
        isDummyField: true,
        text: t('action'),
        headerAlign: 'center',
        classes: 'text-center',
        formatter: (cellData: any, row: any) => {
          return (
            <ProtectedButton
              permissions={Authorization.apps.edit}
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteAppDialog(t, confirmDialogPropsSetter, setApps, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          )
        },
      },
    ]
  }, [t, userPermissions, confirmDialogPropsSetter])

  const data = apps.map((subTab: any) => {
    return { id: subTab.app.id, name: subTab.app.name, type: appDefinitions.find((d) => d.type === subTab.app.type)?.name, authmethod: subTab.app.authmethod }
  })

  const defaultSorted = [
    {
      dataField: 'name',
      order: 'asc',
    },
  ]

  return (
    <Loading isLoading={loading}>
      <Switch>
        {apps.map((tab) => (
          <ProtectedRoute key={tab.path} path={tab.path} permissions={tab.permissions} Component={tab.component} props={tab.props} />
        ))}
        <Route path={match.path}>
          <DataTable
            data={data}
            columns={columns}
            caption={t('app.text_plural')}
            helpLink={t('app.helpLink')}
            searchable={false}
            defaultSorted={defaultSorted}
            captionButtons={[
              {
                onClick: () => createAppDialog(t, editDialogPropsSetter, setApps, appDefinitions),
                text: t('new'),
                permissions: Authorization.apps.edit,
              },
            ]}
          />
        </Route>
      </Switch>
    </Loading>
  )
}

function createAppDialog(t: TFunction, editDialogPropsSetter: any, setSubTabs: React.Dispatch<any>, appDefinitions: any[]) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('app.createHeader'),
    onSubmit: AppService.createApp,
    onSubmitExtraArgs: [t, setSubTabs],
    data: (custom: string[]) => [
      {
        id: 'type',
        type: 'select',
        options: appDefinitions.map((definition) => {
          return { label: definition.name, value: definition.type }
        }),
        onChange: (e: any, setCustom: React.Dispatch<any>) => setCustom([e.target.value, custom[1]]),
        value: custom[0],
        label: t('type.text'),
        required: true,
      },
      ...getEditData(appDefinitions, custom),
    ],
    labelGridSize: 4,
    initCustom: [appDefinitions[0].type, false],
  }

  editDialogPropsSetter(editDialogProps)
}

function getEditData(appDefinitions: any[], custom: string[]) {
  return appDefinitions
    .map((definition) => {
      definition.attributes.forEach((attribute: any) => (attribute.definitionType = definition.type))
      return definition.attributes
    })
    .reduce((accumulator, currentValue) => accumulator.concat(currentValue), [])
    .filter((attribute: any) => attribute.name !== 'type' && attribute.definitionType === custom[0])
    .map((attribute: any) => getAttributeData(attribute, custom))
    .filter((val: any) => Object.keys(val).length > 0)
}

function getAttributeData(attribute: any, custom: string[]) {
  if (custom[1] && attribute.name === 'collectionName') {
    return {}
  }

  if (attribute.name === 'disableIndexing') {
    attribute.value = custom[1]
    attribute.onChange = (e: any, setCustom: React.Dispatch<any>) => setCustom([custom[0], e])
  }
  return getDataField(attribute, attribute.definitionType)
}

function deleteAppDialog(t: TFunction, confirmDialogPropsSetter: any, setSubTabs: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('app.deleteHeader'),
    body: t('app.deleteBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: AppService.deleteApp,
    onSubmitExtraArgs: [row, t, setSubTabs],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

Apps.subTabs = AppService.getApps

export default Apps
